import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useClaimBadgeWhenComplete } from "@/hooks/useBadges";
import type { Checklist } from "./checklist";
import { useChecklistFlag, useGettingStarted } from "./useGettingStarted";

/**
 * The worker checklist the top bar should show, or null. Worker steps come
 * first: while this is non-null the avatar carries the ring and the org
 * checklist pill stays out of the way, so only one checklist asks for
 * attention at a time.
 *
 * completedSeen is captured ONCE, at the first render where the hook is ready
 * (it is "loading" at mount while profile/channels/chat load). The "all set"
 * line shows on the load where the list first completes; on later loads the
 * flag is already set and the checklist is gone.
 */
export function useWorkerChecklist(): Checklist | null {
  const { isAdmin } = useAuth();
  const gs = useGettingStarted({ enabled: !isAdmin });
  const markFlag = useChecklistFlag();
  const seenAtReady = useRef<boolean | null>(null);
  if (seenAtReady.current === null && gs.status === "ready") {
    seenAtReady.current = gs.completedSeen;
  }
  const complete = gs.status === "ready" && gs.checklist.complete;
  useEffect(() => {
    if (complete) markFlag("completedSeen");
  }, [complete, markFlag]);
  // The top bar mounts this on every page, so a finished list earns its
  // badge on the next load, whenever it was finished.
  useClaimBadgeWhenComplete("ready", null, !isAdmin && complete);

  if (isAdmin || gs.status !== "ready") return null;
  if (gs.checklist.complete && seenAtReady.current === true) return null;
  return gs.checklist;
}
